/** The builder's fields (ADR 0020): what can be compared, the comparisons each offers, and words for a summary. */
import type { TFunction } from "i18next";
import { COMPUTED_KEYS } from "../core/computed";
import type { DayRef } from "../core/filter";
import type { Level, PropDef, Task } from "../core/schema";
import { listTaskTypes } from "../registry/taskTypes";
import { signalLabel, type SignalDef } from "../signals/describe";
import type { FieldKind, Group, Item, Row } from "./model";

/** Everything the builder offers choices from; made by `useFilterSources`. */
export interface FilterSources {
  propDefs: PropDef[];
  groups: Record<string, { title: string }>;
  sequences: Record<string, { title: string }>;
  tasks: Task[];
  signals: Record<string, SignalDef>;
  level: Level;
  /** how many tasks match, and the first few titles: the live preview */
  preview?(ast: unknown): { count: number; titles: string[] };
}

export interface FieldDef {
  id: string;
  label: string;
  section: "task" | "where" | "props" | "computed" | "signals";
  kind: FieldKind | "type" | "text" | "group" | "seq" | "parent";
  /** choices, for choice fields */
  options?: { value: string; label: string }[];
  unit?: string;
}

const COMPUTED_BOOL = new Set(["blocked", "active"]);
export const computedBoolKeys = () => new Set(COMPUTED_BOOL);
export const tagKeysOf = (defs: PropDef[]) => new Set(defs.filter((d) => d.kind === "tag").map((d) => d.id));

export function fieldsOf(src: FilterSources, t: TFunction): FieldDef[] {
  const byTitle = (m: Record<string, { title: string }>) =>
    Object.entries(m)
      .map(([value, g]) => ({ value, label: g.title }))
      .sort((a, b) => a.label.localeCompare(b.label));
  const out: FieldDef[] = [
    { id: "done", label: t("filters.field.done"), section: "task", kind: "flag" },
    { id: "archived", label: t("filters.field.archived"), section: "task", kind: "flag" },
    {
      id: "type",
      label: t("filters.field.type"),
      section: "task",
      kind: "type",
      options: listTaskTypes().map((x) => ({ value: x.id, label: t(x.label, { defaultValue: x.id }) })),
    },
    { id: "text", label: t("filters.field.text"), section: "task", kind: "text" },
    {
      id: "group",
      label: t("filters.field.group"),
      section: "where",
      kind: "group",
      options: byTitle(src.groups),
    },
    {
      id: "seq",
      label: t("filters.field.seq"),
      section: "where",
      kind: "seq",
      options: byTitle(src.sequences),
    },
    { id: "parent", label: t("filters.field.parent"), section: "where", kind: "parent" },
  ];
  for (const d of src.propDefs) {
    if (d.spec.hidden) continue;
    const label = t(`props.${d.id}`, { defaultValue: d.name });
    const kind: FieldKind =
      d.kind === "enum"
        ? d.spec.multi
          ? "multi"
          : "enum"
        : d.kind === "number"
          ? "number"
          : d.kind === "datetime"
            ? "date"
            : d.kind === "tag"
              ? "bool"
              : d.kind === "geo"
                ? "geo"
                : "text";
    const values = (d.spec.values as { id: string; label: string }[] | undefined) ?? [];
    out.push({
      id: `prop:${d.id}`,
      label,
      section: "props",
      kind,
      options: values.map((v) => ({
        value: v.id,
        label: t(`enum.${d.id}.${v.id}`, { defaultValue: v.label }),
      })),
    });
  }
  for (const k of COMPUTED_KEYS)
    out.push({
      id: `prop:${k}`,
      label: t(`filters.computed.${k}`),
      section: "computed",
      kind: COMPUTED_BOOL.has(k) ? "bool" : "number",
    });
  for (const [name, def] of Object.entries(src.signals).sort(([a], [b]) => a.localeCompare(b)))
    out.push({
      id: `signal:${name}`,
      label: signalLabel(name, def, t),
      section: "signals",
      kind: "signal",
      unit: def.unit,
    });
  return out;
}

/** The kind of a signal field: from its definition. */
export function signalKind(src: FilterSources, field: string): "boolean" | "number" | "text" {
  return src.signals[field.slice(7)]?.type ?? "text";
}

/** The comparisons a field offers, in the order shown. */
export function opsFor(f: FieldDef | undefined, src: FilterSources): string[] {
  if (!f) return [];
  switch (f.kind) {
    case "flag":
      return ["is"];
    case "type":
      return ["eq", "neq"];
    case "text":
      return f.id === "text" ? ["contains", "notContains"] : ["eq", "neq", "set", "notSet"];
    case "group":
      return ["member", "notMember", "anyOf"];
    case "seq":
      return ["member", "notMember"];
    case "parent":
      return ["eq", "neq", "top"];
    case "enum":
      return ["eq", "neq", "in", "notIn", "set", "notSet"];
    case "multi":
      return ["has", "in", "notIn", "set", "notSet"];
    case "number":
      return ["eq", "neq", "gte", "lte", "gt", "lt", "set", "notSet"];
    case "date":
      return ["before", "after", "on", "onOrBefore", "onOrAfter", "next", "last", "between", "set", "notSet"];
    case "bool":
      return ["yes", "no"];
    case "geo":
      return ["set", "notSet"];
    case "signal": {
      const k = signalKind(src, f.id);
      return k === "number" ? ["eq", "neq", "gte", "lte", "gt", "lt"] : ["eq", "neq"];
    }
  }
  return [];
}

/** A sensible starting value when a field or comparison is picked. */
export function defaultValue(f: FieldDef | undefined, op: string, src: FilterSources): unknown {
  if (!f) return undefined;
  if (f.kind === "flag") return false;
  if (["set", "notSet", "yes", "no", "top"].includes(op)) return undefined;
  if (op === "in" || op === "notIn" || op === "anyOf") return [];
  if (op === "next" || op === "last") return 7;
  if (op === "between") return [{ days: 0 }, { days: 7 }];
  if (f.kind === "date") return { days: 0 };
  if (f.kind === "signal" && signalKind(src, f.id) === "boolean") return true;
  if (f.options?.length && ["eq", "neq", "has", "member", "notMember"].includes(op))
    return f.options[0].value;
  return undefined;
}

export function dayText(x: DayRef | undefined, t: TFunction): string {
  if (typeof x === "string") return x;
  if (!x) return "…";
  const n = x.days;
  return n === 0
    ? t("filters.day.today")
    : n === 1
      ? t("filters.day.tomorrow")
      : n === -1
        ? t("filters.day.yesterday")
        : n > 0
          ? t("filters.day.inDaysText", { n })
          : t("filters.day.daysAgoText", { n: -n });
}

function valueText(r: Row, f: FieldDef | undefined, src: FilterSources, t: TFunction): string {
  const opt = (v: unknown) => f?.options?.find((o) => o.value === v)?.label ?? String(v);
  const v = r.value;
  if (r.op === "next" || r.op === "last") return t("filters.daysText", { n: Number(v) });
  if (r.op === "between" && Array.isArray(v)) return `${dayText(v[0], t)} – ${dayText(v[1], t)}`;
  if (f?.kind === "date") return dayText(v as DayRef, t);
  if (f?.kind === "flag" || (f?.kind === "signal" && typeof v === "boolean"))
    return v ? t("filters.yes") : t("filters.no");
  if (f?.kind === "parent") return src.tasks.find((x) => x.id === v)?.title ?? String(v);
  if (Array.isArray(v)) return v.map(opt).join(", ");
  return opt(v);
}

/** A row in words: "Energy at most 2", "Context is any of @home, @phone". */
export function rowText(r: Row, fields: FieldDef[], src: FilterSources, t: TFunction): string {
  const f = fields.find((x) => x.id === r.field);
  const label = f?.label ?? r.field;
  if (r.field === "done" || r.field === "archived")
    return r.value ? t(`filters.flag.${r.field}`) : t(`filters.flag.not_${r.field}`);
  const op = t(`filters.op.${r.op}`);
  if (["set", "notSet", "yes", "no", "top"].includes(r.op)) return `${label} ${op}`;
  return `${label} ${op} ${valueText(r, f, src, t)}`;
}

/** The whole filter in words, for the line shown in a form. */
export function groupText(
  g: Group,
  fields: FieldDef[],
  src: FilterSources,
  t: TFunction,
  top = true,
): string {
  const parts = g.items.map((it: Item) =>
    it.kind === "row"
      ? rowText(it, fields, src, t)
      : it.kind === "raw"
        ? t("filters.rawShort")
        : `(${groupText(it, fields, src, t, false)})`,
  );
  if (!parts.length) return top ? t("filters.everyTask") : "";
  const joined = parts.join(g.mode === "any" ? ` ${t("filters.or")} ` : ` ${t("filters.and")} `);
  return g.mode === "none" ? t("filters.noneOf", { list: parts.join(", ") }) : joined;
}
